import ReactDOM from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

// Redux + MUI + etc
import { ThemeProvider } from "@mui/material";
import { configureStore } from "@reduxjs/toolkit";
import { SnackbarProvider } from "notistack";
import { Provider } from "react-redux";

// === Stytch B2B imports ===
import { StytchB2BProvider } from "@stytch/react/b2b";
import { StytchB2BUIClient } from "@stytch/vanilla-js/b2b";

// Your own modules
import AuthenticatedDashboard from "./AuthenticatedDashboard";
import ProtectedRoute from "./authentication/ProtectedRoute";
import generateMuiThemeDefaults from "./functions/generateMuiThemeDefaults";
import "./index.css";
import rootReducer from "./redux/reducers";
import CreditAuthorizationFormScreen from "./screens/CreditAuthorizationFormScreen";
import LoanApplicationFormScreen from "./screens/LoanApplicationFormScreen";
import LoginScreen from "./screens/LoginScreen";

// AG Grid
import { LicenseManager } from "ag-grid-enterprise";
import Authenticate from "./authentication/Authenticate";
import { loanSizerTypeEnums } from "./config/enums/loanSizerTypeEnums";
import CreateAccountScreen from "./screens/CreateAccountScreen";
import CreditAuthSubmissionsScreen from "./screens/CreditAuthSubmissionsScreen";
import DiscoveryOrganizationHandler from "./screens/DiscoveryOrganizationHandler";
import LoanAppSubmissionsScreen from "./screens/LoanAppSubmissionsScreen";
import LoanDrilldownScreen from "./screens/LoanDrilldownScreen/LoanDrilldownScreen";
import LoanIntakePricingSizerScreen from "./screens/LoanIntakePricingSizerScreen";
import LoanPipelineScreen from "./screens/LoanPipelineScreen/LoanPipelineScreen";

import LoanQuickIntakeFormScreen from "./screens/LoanQuickIntakeFormScreen";
import GlobalProcessingScreen from "./screens/GlobalProcessingScreen/GlobalProcessingScreen";
LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_ENTERPRISE_KEY);

// Create Redux store
const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
      immutableCheck: false,
    }),
  devTools: process.env.NODE_ENV !== "production",
});

const stytchPublicToken = process.env.REACT_APP_STYTCH_PUBLIC_LIVE_TOKEN;

const stytchOptions = {
  cookieOptions: {
    opaqueTokenCookieName: "stytch_session",
    jwtCookieName: "stytch_session_jwt",
    path: "",
    availableToSubdomains: false,
    domain: "",
  },
};

// Create the B2B client
const stytchB2BClient = new StytchB2BUIClient(stytchPublicToken, stytchOptions);

const primaryColor = "#2962ff";
// MUI-themed wrapper

// Mount the app
const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <StytchB2BProvider stytch={stytchB2BClient}>
    <Provider store={store}>
      <BrowserRouter>
        <SnackbarProvider maxSnack={5}>
          <ThemeProvider theme={generateMuiThemeDefaults({ primaryColor })}>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<LoginScreen />} />
              <Route path="/sign-up" element={<CreateAccountScreen />} />
              <Route
                path="/loan-application/*"
                element={<LoanApplicationFormScreen />}
              />
              <Route
                path="/credit-auth/*"
                element={<CreditAuthorizationFormScreen />}
              />
              <Route
                path="/discovery-organization-handler"
                element={<DiscoveryOrganizationHandler />}
              />

              <Route
                path="/rtl-sizer"
                element={<LoanIntakePricingSizerScreen showLogo />}
              />
              <Route
                path="/rtl-sizer/handsome-homebuyer"
                element={
                  <LoanIntakePricingSizerScreen
                    showLogo
                    type={loanSizerTypeEnums.HANDSOME_HOME_BUYER}
                  />
                }
              />
              {/* Stytch magic link callback */}
              <Route path="/authenticate" element={<Authenticate />} />

              {/* Private/Protected Routes */}
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <AuthenticatedDashboard>
                      <Routes>
                        <Route
                          path={"/loans"}
                          element={<LoanPipelineScreen />}
                        />

                        <Route
                          path={"/loan-applications"}
                          element={<LoanAppSubmissionsScreen />}
                        />
                        <Route
                          path={"/credit-authorizations"}
                          element={<CreditAuthSubmissionsScreen />}
                        />
                        <Route
                          path={"/processing/*"}
                          element={<GlobalProcessingScreen />}
                        />
                        {/* <Route
                          path={"/processing/exceptions"}
                          element={<GlobalProcessingScreen />}
                        />
                        <Route
                          path={"/processing/uploaded-sizers"}
                          element={<GlobalProcessingScreen />}
                        />
                        <Route
                          path={"/processing/tasks"}
                          element={<GlobalProcessingScreen />}
                        /> */}
                        <Route
                          path="/loan/:id/*"
                          element={<LoanDrilldownScreen />}
                        />
                        <Route
                          path="/loan/create"
                          element={<LoanQuickIntakeFormScreen />}
                        />
                        <Route
                          path="/loan/pricing-sizer"
                          element={<LoanIntakePricingSizerScreen />}
                        />

                        <Route
                          path={"/resources"}
                          element={<LoanPipelineScreen />}
                        />
                        <Route path="*" element={<Navigate to="/loans" />} />
                      </Routes>
                    </AuthenticatedDashboard>
                  </ProtectedRoute>
                }
              />

              {/* Redirect unknown paths */}
              {/* <Route path="*" element={<Navigate to="/login" />} /> */}
            </Routes>
          </ThemeProvider>
        </SnackbarProvider>
      </BrowserRouter>
    </Provider>
  </StytchB2BProvider>
);
